import { useRef, useState } from 'react';
import { RegionalDivision } from '../../types/schema';
import { useRegionAndLocationContext } from '../../contexts/RegionAndLocationContext';
import { Btn, BtnIconOnly, IconClose, InputField } from '../ui';

interface AddRegionModalProps {
    region?: RegionalDivision;
    onClose: () => void;
    onSubmit: (name: string, description: string) => void;
}

export const AddRegionModal = ({ region, onClose, onSubmit }: AddRegionModalProps) => {
    const { updateRegionInDatabase } = useRegionAndLocationContext();
    const [name, setName] = useState(region?.name || '');
    const [description, setDescription] = useState(region?.description || '');
    const [saving, setSaving] = useState(false);
    /** Only dismiss when press + release both land on the backdrop (not after drag-select from a field). */
    const dismissOnClickRef = useRef(false);

    const isEdit = !!region;

    const handleSubmit = async () => {
        if (!name.trim()) return;
        setSaving(true);
        try {
            if (isEdit) {
                await updateRegionInDatabase(region.id, { name: name.trim(), description: description.trim() || null });
                onClose();
            } else {
                await onSubmit(name.trim(), description.trim());
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="modal-overlay"
            onMouseDown={(e) => {
                dismissOnClickRef.current = e.target === e.currentTarget;
            }}
            onClick={(e) => {
                if (dismissOnClickRef.current && e.target === e.currentTarget) {
                    onClose();
                }
                dismissOnClickRef.current = false;
            }}
        >
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{isEdit ? 'Edit Region' : 'Add Region'}</h2>
                    <BtnIconOnly size="sm" icon={<IconClose />} ariaLabel="Close" onClick={onClose} />
                </div>
                <div className="modal-body">
                    <InputField
                        label="Region Name"
                        placeholder="e.g. West Texas"
                        value={name}
                        onChange={setName}
                        autoFocus
                    />
                    <div className="modal-field">
                        <label>Region Description</label>
                        <textarea
                            placeholder="Optional description of operating area"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                        />
                    </div>
                </div>
                <div className="modal-footer">
                    <Btn variant="secondary-outlined" buttonText="Cancel" buttonOnClick={onClose} />
                    <Btn
                        variant="primary"
                        buttonText={saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Region'}
                        buttonDisabled={!name.trim() || saving}
                        buttonOnClick={handleSubmit}
                    />
                </div>
            </div>
        </div>
    );
};
